import { useFocusPageHeading } from '@common/hooks/useFocusPageHeading';
import { analytics } from '@features/analytics';
import { useTranslations } from '@lang';
import { FC } from 'react';
import { useRouter } from 'shared-routing';
import { RoomUpgradeTemplate } from 'voyager';
import type { RoomUpgradeOffer } from 'voyager';
import { UpgradeItemV2Props } from './UpgradeItemV2.types';
import { useRoomUpgradeDialogLabels, useRoomUpgradeOffer } from './useRoomUpgradeOffer';

export const UpgradeItemV2: FC<UpgradeItemV2Props> = ({
  isOpen,
  id,
  imageUrl,
  name,
  description,
  amenities,
  priceDifference,
  pricePerStay,
  pricePerNight,
  quantity,
  displayUrgencyMessaging,
  maxOccupancy,
  maxAdults,
  maxChildren,
  beds,
  linkRoute,
  linkParams,
  upgradeLinkParams,
  originalRoom,
  trackingItem,
  language,
  numberOfNights,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const dialogLabels = useRoomUpgradeDialogLabels();
  const upgradeOffer = useRoomUpgradeOffer({
    name,
    imageUrl,
    description,
    amenities,
    beds,
    priceDifference,
    pricePerStay,
    pricePerNight,
    quantity,
    displayUrgencyMessaging,
    maxOccupancy,
    maxAdults,
    maxChildren,
  });

  const priceDifferencePerNight =
    priceDifference && numberOfNights && numberOfNights > 0
      ? { ...priceDifference, amount: priceDifference.amount / numberOfNights }
      : undefined;

  useFocusPageHeading(isOpen ? 'upgrade' : undefined);

  if (!isOpen) return null;

  const handleSelection = (accepted: boolean) => {
    analytics.track({
      event: 'upgrade_selection',
      params: {
        upgrade_source: 'booking_step',
        items: [
          {
            original_item_name: originalRoom?.name ?? trackingItem?.name ?? 'unknown',
            original_item_id: originalRoom?.id ?? trackingItem?.id ?? 'unknown',
            item_name: name,
            item_id: id,
            price_difference: priceDifference,
            price_per_stay: pricePerStay,
            accepted,
          },
        ],
      },
    });
    router.push(
      { pathname: linkRoute, query: accepted ? upgradeLinkParams : linkParams },
      undefined,
      {
        shallow: !accepted,
      }
    );
  };

  return (
    <RoomUpgradeTemplate
      locale={language}
      originalRoom={
        originalRoom?.name
          ? { name: originalRoom.name, imageUrl: originalRoom.imageUrl }
          : undefined
      }
      upgradeRoom={{ ...(upgradeOffer as RoomUpgradeOffer), priceDifferencePerNight }}
      onBack={() => router.back()}
      onSkip={() => handleSelection(false)}
      onUpgrade={() => handleSelection(true)}
      labels={{
        ...dialogLabels,
        title: t('room.upgrade.title'),
        titleAccent: t('room.upgrade.subtitle'),
        backAriaLabel: t('common.back'),
        seeDetails: t('room.upgrade.showDetails'),
        upgradeFor: t('room.upgrade.upgradeFor'),
        forStay: t('room.upgrade.forStay'),
        noImage: t('basket.noImageLabel'),
      }}
    />
  );
};
